import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  ArrowLeft, Download, Share2, Anchor, Compass, Trophy, Flag,
  Calendar, Target, Sparkles, Flame,
} from 'lucide-react';

const KIND_META = {
  start: { icon: Anchor, label: 'Embarquement' },
  chapter: { icon: Compass, label: 'Chapitre' },
  milestone: { icon: Flag, label: 'Jalon' },
  victory: { icon: Trophy, label: 'Victoire' },
  event: { icon: Calendar, label: 'Événement' },
  objective: { icon: Target, label: 'Objectif' },
  challenge: { icon: Target, label: 'Défi' },
  xp: { icon: Sparkles, label: 'XP' },
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

const JourneyPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const friseRef = useRef(null);

  useEffect(() => {
    api.get('/users/me/journey')
      .then((res) => setData(res.data))
      .catch(() => toast.error('Impossible de charger ta frise'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildImage = async () => {
    const node = friseRef.current;
    return toPng(node, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: getComputedStyle(document.body).backgroundColor,
      filter: (el) => !el?.dataset?.noExport,
    });
  };

  const handleDownload = async () => {
    setExporting(true);
    try {
      const dataUrl = await buildImage();
      const link = document.createElement('a');
      link.download = `frise-odys-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
      toast.success('Frise enregistrée en image');
    } catch (e) {
      toast.error("L'export a échoué, réessaie");
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    setExporting(true);
    try {
      const dataUrl = await buildImage();
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], 'frise-odys.png', { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Mon odyssée Odys' });
      } else {
        await handleDownload();
      }
    } catch (e) {
      toast.error('Partage impossible sur cet appareil');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6 space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-20 rounded-xl bg-secondary/50 animate-pulse max-w-3xl mx-auto" />
        ))}
      </div>
    );
  }

  const member = data?.member || {};
  const stats = data?.stats || {};
  const steps = data?.steps || [];
  const initials = (member.name || 'EO').split(' ').map((p) => p[0]).slice(0, 2).join('');

  return (
    <div className="min-h-screen bg-background" data-testid="journey-page">
      <header className="border-b border-border bg-card/70 backdrop-blur-xl sticky top-0 z-10" data-no-export="true">
        <div className="max-w-3xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate('/profile')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-heading font-bold text-base sm:text-xl truncate">Ma Frise de Voyage</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {stats.steps} étape{stats.steps > 1 ? 's' : ''} · {stats.days_aboard} jours à bord
              </p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={handleShare}
              disabled={exporting}
              data-testid="journey-share"
            >
              <Share2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Partager</span>
            </Button>
            <Button size="sm" onClick={handleDownload} disabled={exporting} data-testid="journey-download">
              <Download className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Exporter</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Zone exportée en image */}
        <div
          ref={friseRef}
          className="rounded-2xl border border-border bg-card overflow-hidden"
          data-testid="journey-canvas"
        >
          {/* Cartouche */}
          <div className="px-5 py-5 bg-secondary/40">
            <div className="flex items-center gap-3">
              <Avatar className="w-14 h-14 border-2" style={{ borderColor: 'hsl(var(--gold))' }}>
                <AvatarImage src={member.avatar_url} />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="font-heading font-bold text-lg truncate">{member.name}</p>
                <p className="text-xs uppercase tracking-[0.18em] text-primary">
                  Niveau {member.level} · {member.points?.toLocaleString('fr-FR')} XP
                </p>
                <p className="text-xs text-muted-foreground">
                  À bord depuis le {member.joined_at ? formatDate(member.joined_at) : '—'}
                </p>
              </div>
            </div>

            <div className="filet-gold my-4" />

            <div className="grid grid-cols-4 gap-2 text-center">
              {[
                { value: stats.chapters_completed, label: 'Chapitres' },
                { value: stats.victories, label: 'Victoires' },
                { value: stats.milestones, label: 'Jalons' },
                { value: stats.days_aboard, label: 'Jours' },
              ].map((s) => (
                <div key={s.label}>
                  <p className="font-heading font-bold text-xl">{s.value ?? 0}</p>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Frise */}
          <div className="px-5 py-6">
            {steps.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                Ton odyssée commence : valide un chapitre pour écrire la première ligne.
              </p>
            ) : (
              <div className="relative pl-7">
                <div
                  className="absolute left-[11px] top-1 bottom-1 w-[2px] rounded-full"
                  style={{ background: 'linear-gradient(hsl(var(--gold)), hsl(var(--primary)))' }}
                />
                <div className="space-y-5">
                  {steps.map((step, index) => {
                    const meta = KIND_META[step.kind] || KIND_META.xp;
                    const Icon = meta.icon;
                    return (
                      <div key={`${step.date}-${index}`} className="relative" data-testid={`journey-step-${index}`}>
                        <span
                          className="absolute -left-7 top-0.5 w-6 h-6 rounded-full flex items-center justify-center"
                          style={{
                            background: 'hsl(var(--card))',
                            border: '2px solid hsl(var(--gold))',
                            color: 'hsl(var(--gold-dark))',
                          }}
                        >
                          <Icon className="w-3 h-3" />
                        </span>
                        <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
                          {formatDate(step.date)} · {meta.label}
                        </p>
                        <p className="text-sm font-medium break-words">{step.title}</p>
                        {step.subtitle && (
                          <p className="text-xs text-muted-foreground break-words">{step.subtitle}</p>
                        )}
                        {step.xp > 0 && (
                          <p className="text-xs text-primary font-medium">+{step.xp} XP</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Pied de frise */}
          <div className="px-5 py-3 border-t border-border flex items-center justify-between gap-2 bg-secondary/30">
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Flame className="w-3.5 h-3.5 text-primary" />
              Meilleure série : {stats.best_streak || 0} jours
            </span>
            <span className="font-heading text-xs font-semibold">L&apos;Équipage Odys</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default JourneyPage;
